import React, { useEffect, useMemo, useRef, useState } from "react"
import { Button, Drawer } from "antd"

import type { AxisLeaf } from "utils/tableModel"
import type { EngineOutputItem } from "types/engineOutput.types"
import { Loader } from "shared/ui/Loader/Loader.component"
import { ACCENT } from "constants/brand"
import { LineageLogEntry, LineageSourceRole, QnrField } from "types/lineage.types"
import { RunExclusive, useQuestionnaireMetadataQuery, useSessionLineageQuery } from "../api/useLineage"
import { lookupByLabel, lookupFamily, normLabel } from "../utils/lineageLabels"
import { commandLine, SessionCall, sessionCalls } from "../utils/lineageCalls"
import {
  idSegments,
  PreparationScript,
  preparationPart,
  scriptLabel,
  TableTrace,
  TraceCategory,
  TraceSide,
  TraceStep,
  traceTable,
  TraceRequest,
  TraceVariable,
} from "../utils/traceTable"
import {
  CheckNumbers,
  checkPlan,
  matchesShown,
  preparationWeight,
  readCheck,
  shownDecimals,
  traceCheckScript,
} from "../utils/traceCheck"
import LineageModal from "./LineageModal.component"

export type { TraceRequest } from "../utils/traceTable"

export interface TraceNumberPanelProps {
  /** The cell to trace; the panel is open while one is set. */
  request: TraceRequest | null
  onClose: () => void
  /** The engine session the table ran in. */
  instance: string | null
  projectId: string
  /** The description of a helper name the feature's compiler made. */
  describeHelper?: (name: string) => string | undefined
  /** The feature's own name for a script, from its text. */
  describeScript?: (script: string) => string | undefined
  /** What the session's preparation sent, part by part. */
  preparation?: PreparationScript[]
  /** Runs the table again, offered when it is no longer in the session's lineage. */
  onRunAgain?: () => void
  /**
   * Runs a script in the table's session, after what is queued there, and gives its output: the
   * check that works out the cell's numbers (see `traceCheck`). Without it the panel does not
   * show the calculation.
   */
  runCheck?: (script: string) => Promise<EngineOutputItem[]>
  /**
   * Runs the read of the session's lineage in its turn with the session's other calls (see
   * `RunExclusive`): after what is queued there, and before the check. Without it the lineage is
   * read straight away.
   */
  runExclusive?: RunExclusive
}

const MAX_LISTED = 3

const roleWords: Record<LineageSourceRole, string> = {
  operand: "reads",
  filter: "filter",
  universe: "base",
  weight: "weight",
}

const roleChipClass: Record<LineageSourceRole, string> = {
  operand: "bg-slate-50 text-slate-600 border-slate-200",
  filter: "bg-amber-50 text-amber-700 border-amber-200",
  weight: "bg-violet-50 text-violet-700 border-violet-200",
  universe: "bg-sky-50 text-sky-700 border-sky-200",
}

/** The labels of a leaf, without its statistic. */
const leafLabels = (leaf: AxisLeaf): string => leaf.path.filter((p) => p !== "").join(" › ")

/** The questionnaire's fields by their matching form. */
const fieldIndexOf = (fields: QnrField[] | undefined): Map<string, QnrField> => {
  const m = new Map<string, QnrField>()
  fields?.forEach((f) => {
    const k = normLabel(f.label)
    if (!m.has(k)) m.set(k, f)
  })
  return m
}

/** Whether `answer` is an answer of `field`, or of its family (one variable per answer). */
const hasAnswer = (index: Map<string, QnrField>, question: string, answer: string): boolean | null => {
  const field = lookupByLabel(index, question)
  if (field) return field.options.some((o) => normLabel(o.label) === normLabel(answer))
  const family = lookupFamily(index, question)
  if (family.length > 0)
    return family.some((f) => {
      const segments = f.label.split("//")
      return normLabel(segments[segments.length - 1]) === normLabel(answer)
    })
  return null
}

interface Context {
  qnrIndex: Map<string, QnrField> | null
  /** The session's calls (see `sessionCalls`): the command log's calls, or a schema 3 script log */
  calls: SessionCall[]
  /** The command log (schema 4) */
  log?: LineageLogEntry[]
  labelOf: (index: number) => string
  /** What call `index` ran, as the panel shows it */
  textOf: (index: number) => string
}

const Heading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 className="mb-2 mt-6 text-[13px] font-medium text-slate-700">{children}</h3>
)

const TOGGLE_CLASS =
  "rounded text-[11px] text-slate-500 underline-offset-2 hover:text-slate-700 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--dq-brand)]"

/** A text button that shows or hides what follows it. */
const Toggle: React.FC<{ open: boolean; onClick: () => void; children: React.ReactNode }> = ({
  open,
  onClick,
  children,
}) => (
  <button type="button" className={TOGGLE_CLASS} aria-expanded={open} onClick={onClick}>
    {children}
  </button>
)

// ---------------------------------------------------------------------------
// Steps in words
// ---------------------------------------------------------------------------

/** A few names in quotes: "A", "B" and 3 more. */
const nameList = (variables: TraceVariable[]): string => {
  const names = [...new Set(variables.map((v) => `"${v.label}"`))]
  if (names.length <= MAX_LISTED) {
    if (names.length <= 1) return names.join("")
    return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`
  }
  return `${names.slice(0, MAX_LISTED).join(", ")} and ${names.length - MAX_LISTED} more`
}

/** What one step did, in one sentence. */
export const stepSentence = (step: TraceStep): string => {
  const of = (role: LineageSourceRole) => step.sources.filter((s) => s.role === role).map((s) => s.variable)
  const operands = of("operand")
  const filters = [...of("filter"), ...of("universe")]
  const from = nameList(operands)
  const forThose = filters.length > 0 ? ` for those in ${nameList(filters)}` : ""
  const command = step.command.toUpperCase()
  const verb = ((): string => {
    switch (command) {
      case "ASSIGN":
        return from ? `Set from ${from}` : "Set"
      case "OR":
        return from ? `Added those in ${from}` : "Added"
      case "AND":
        return from ? `Kept only those also in ${from}` : "Narrowed"
      case "NOT":
        return from ? `Set to those not in ${from}` : "Reversed"
      case "CALC":
        return from ? `Worked out from ${from}` : "Worked out"
      case "CALCWEIGHTS":
        return from ? `Weights worked out from ${from}` : "Weights worked out"
      case "APPEND":
      case "APPENDSTRUCT":
      case "COPY":
      case "COPYSTRUCT":
        return from ? `Copied from ${from}` : "Copied"
      case "FFIELD":
        return from ? `Combined into one from ${from}` : "Combined into one"
      case "XTAB":
      case "DTAB":
        return from ? `Tabulated ${from}` : "Tabulated"
      default:
        return from ? `Made from ${from} (${command})` : `Made (${command})`
    }
  })()
  return `${verb}${forThose}.`
}

const TOKEN = /<<.*?>>|"[^"]*"|\S+/g

/**
 * The command that made the step: the one the command log recorded it from (schema 4), else the
 * line of the step's script that made it, or the step written out when none is found.
 */
const commandText = (step: TraceStep, ctx: Context): string => {
  const logged = step.entry !== undefined ? ctx.log?.[step.entry] : undefined
  if (logged) return commandLine(logged)
  const written = [
    step.command,
    step.target,
    ...step.sources.map((s) => (s.role === "operand" ? s.variable.id : `${roleWords[s.role]}: ${s.variable.id}`)),
  ].join(" ")
  const text = step.script !== undefined ? ctx.calls[step.script]?.text : undefined
  if (!text) return written
  const target = normLabel(step.target)
  const candidates = text.split(/\r?\n/).filter((line) => {
    const tokens = line.trim().match(TOKEN) ?? []
    return tokens[0]?.toUpperCase() === step.command.toUpperCase() && normLabel(tokens[1] ?? "") === target
  })
  const reads = (line: string): boolean => {
    const tokens = new Set((line.match(TOKEN) ?? []).map(normLabel))
    return step.sources.every((s) => tokens.has(normLabel(s.variable.id)))
  }
  return (candidates.find(reads) ?? candidates[0] ?? written).trim()
}

// ---------------------------------------------------------------------------
// Where the values come from
// ---------------------------------------------------------------------------

/** Where a collected variable comes from: its question, or the answer it is. */
const CollectedLine: React.FC<{ variable: TraceVariable; ctx: Context; answer?: string }> = ({
  variable,
  ctx,
  answer,
}) => {
  const { qnrIndex } = ctx
  const line = (text: string) => <div className="text-[12px] text-slate-500">{text}</div>
  if (!qnrIndex) return line("Came with the data.")
  const field = lookupByLabel(qnrIndex, variable.id)
  if (field) {
    if (answer) return line(`From the questionnaire: the answer "${answer}"${field.text ? ` of ${field.text}` : ""}`)
    return line(`From the questionnaire${field.text ? `: ${field.text}` : ""}`)
  }
  const family = lookupFamily(qnrIndex, variable.id)
  if (family.length > 0)
    return line(`From the questionnaire: one question, ${family.length} variable${family.length === 1 ? "" : "s"}`)
  // An answer used on its own ("Gender › Male"): the question it belongs to.
  const segments = idSegments(variable.id)
  if (segments.length > 1) {
    const question = variable.id.slice(0, variable.id.lastIndexOf("::"))
    const parent = lookupByLabel(qnrIndex, question)
    const own = segments[segments.length - 1]
    if (parent && hasAnswer(qnrIndex, question, own))
      return line(`From the questionnaire: the answer "${own}"${parent.text ? ` of ${parent.text}` : ""}`)
  }
  return line("Not in the questionnaire.")
}

const Dot: React.FC<{ derived: boolean }> = ({ derived }) => (
  <span
    aria-hidden
    className="absolute -left-[5px] top-[4px] h-[9px] w-[9px] rounded-full border"
    style={derived ? { background: ACCENT, borderColor: ACCENT } : { background: "#FFFFFF", borderColor: "#94A3B8" }}
  />
)

/** The variables a variable's steps read, each once, with the role it was first read in. */
const sourcesOf = (variable: TraceVariable): { variable: TraceVariable; role: LineageSourceRole }[] => {
  const seen = new Map<string, { variable: TraceVariable; role: LineageSourceRole }>()
  variable.steps.forEach((step) =>
    step.sources.forEach((s) => {
      if (!seen.has(s.variable.id)) seen.set(s.variable.id, { variable: s.variable, role: s.role })
    })
  )
  return [...seen.values()]
}

/** The levels of the chain that show how they were made without a click: the cell's and the next. */
const OPEN_DEPTH = 1

/**
 * One link of the chain: a variable, one sentence per step that made it (a step that reads
 * nothing, such as the FIELD that created it, is folded in), the scripts that ran them, the
 * commands on demand, and below it the variables it read.
 */
const ChainNode: React.FC<{
  variable: TraceVariable
  ctx: Context
  depth: number
  role?: LineageSourceRole
  answer?: string
}> = ({ variable, ctx, depth, role, answer }) => {
  const derived = variable.steps.length > 0
  const [open, setOpen] = useState(depth <= OPEN_DEPTH)
  const [commands, setCommands] = useState(false)
  const reading = variable.steps.filter((s) => s.sources.length > 0)
  const said = reading.length > 0 ? reading : variable.steps.slice(0, 1)
  const sources = sourcesOf(variable)
  return (
    <li className="relative pb-3 pl-4 last:pb-0">
      <Dot derived={derived || !variable.collected} />
      <div className="flex flex-wrap items-center gap-1.5 text-[12px]">
        {role && role !== "operand" && (
          <span className={`rounded border px-1 py-px text-[10px] font-medium ${roleChipClass[role]}`}>
            {roleWords[role]}
          </span>
        )}
        <span className="font-medium text-slate-800">{variable.label}</span>
        {variable.cycle && <span className="text-[11px] italic text-slate-400">(shown above)</span>}
      </div>
      {variable.collected && !variable.cycle && <CollectedLine variable={variable} ctx={ctx} answer={answer} />}
      {derived && !variable.cycle && !open && (
        <Toggle open={false} onClick={() => setOpen(true)}>
          How it was made
        </Toggle>
      )}
      {derived && !variable.cycle && open && (
        <div className="mt-0.5">
          {said.map((step, i) => (
            <div key={i} className="mb-1">
              <div className="text-[12px] text-slate-600">
                {reading.length > 0 ? stepSentence(step) : "Created."}
                {step.target !== variable.id && normLabel(step.target) !== normLabel(variable.id) && (
                  <span className="text-slate-400"> (on {step.targetLabel})</span>
                )}
              </div>
              {step.script !== undefined && ctx.calls.length > 0 && (
                <span className="mt-0.5 inline-block rounded bg-slate-100 px-1.5 py-px text-[10px] text-slate-500">
                  {ctx.labelOf(step.script)}
                </span>
              )}
            </div>
          ))}
          <Toggle open={commands} onClick={() => setCommands((c) => !c)}>
            {commands ? "Hide the commands" : "Show the commands"}
          </Toggle>
          {commands && (
            <pre className="mt-1 max-h-48 overflow-auto rounded border border-slate-200 bg-slate-50 px-2 py-1.5 font-mono text-[11px] text-slate-700">
              {variable.steps.map((s) => commandText(s, ctx)).join("\n")}
            </pre>
          )}
          {sources.length > 0 && (
            <ol className="ml-[3px] mt-2 border-l border-slate-200">
              {sources.map((s) => (
                <ChainNode key={s.variable.id} variable={s.variable} ctx={ctx} depth={depth + 1} role={s.role} />
              ))}
            </ol>
          )}
        </div>
      )}
    </li>
  )
}

/** One side of the cell: which category it is, and the chain of what it reads. */
const SideChain: React.FC<{
  side: string
  leaf: AxisLeaf
  category: TraceCategory
  reads: TraceSide
  ctx: Context
}> = ({ side, leaf, category, reads, ctx }) => {
  const labels = leafLabels(leaf)
  const head = <div className="mb-1 text-[12px] text-slate-500">{side}: {labels || "Total"}</div>
  if (category.kind === "total")
    return (
      <div className="mb-3">
        {head}
        <div className="text-[12px] text-slate-600">The Total: everyone in the base.</div>
      </div>
    )
  let note: string | null = null
  let answer: string | undefined
  if (reads.whole)
    note = `"${labels}" is not one category of the data (a net, a statistic or a label that could not be matched), so the whole ${reads.variables.length === 1 ? "variable is" : "variables are"} shown.`
  if (category.kind === "answer") {
    const known = ctx.qnrIndex ? hasAnswer(ctx.qnrIndex, category.question.id, category.answer) : null
    if (known === false)
      note = `"${category.answer}" is not an answer of ${category.question.label} in the questionnaire (a net or a relabelled answer), so the whole question is shown.`
    else answer = category.answer
  }
  return (
    <div className="mb-3">
      {head}
      {note && <p className="mb-1.5 text-[12px] text-slate-500">{note}</p>}
      <ol className="ml-[3px] border-l border-slate-200">
        {reads.variables.map((v) => (
          <ChainNode key={v.id} variable={v} ctx={ctx} depth={0} answer={answer} />
        ))}
      </ol>
    </div>
  )
}

/** A compact line (base, weight) that opens to the chains of its variables. */
const CompactLine: React.FC<{ title: string; empty: string; variables: TraceVariable[]; ctx: Context }> = ({
  title,
  empty,
  variables,
  ctx,
}) => {
  const [open, setOpen] = useState(false)
  if (variables.length === 0)
    return (
      <div className="text-[12px] text-slate-600">
        <span className="text-slate-500">{title}:</span> {empty}
      </div>
    )
  return (
    <div className="text-[12px] text-slate-600">
      <span className="text-slate-500">{title}:</span> {variables.map((v) => v.label).join(", ")}{" "}
      <Toggle open={open} onClick={() => setOpen((o) => !o)}>
        {open ? "Hide" : "Show where it comes from"}
      </Toggle>
      {open && (
        <ol className="ml-[3px] mt-1.5 border-l border-slate-200">
          {variables.map((v) => (
            <ChainNode key={v.id} variable={v} ctx={ctx} depth={1} />
          ))}
        </ol>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// How it was worked out
// ---------------------------------------------------------------------------

type CheckState =
  | { status: "running" }
  | { status: "done"; numbers: CheckNumbers }
  | { status: "error"; message: string }

const formatNumber = (n: number, decimals?: number): string =>
  decimals !== undefined
    ? n.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
    : n.toLocaleString("en-US", { maximumFractionDigits: Number.isInteger(n) ? 0 : 2 })

/** What the two numbers of the sum are, by the statistic's kind. */
const sumWords = (numbers: CheckNumbers): { top: string; bottom?: string } => {
  const { plan } = numbers
  const w = (text: string) => (plan.weighted ? `weighted ${text}` : text)
  switch (plan.kind) {
    case "mean":
      return {
        top: plan.weighted ? "weighted sum of the answers" : "sum of the answers",
        bottom: plan.weighted ? "weighted base: people who gave an answer" : "people who gave an answer",
      }
    case "share":
      return {
        top: w(plan.of === "answered" ? "count of those who said yes" : "count in this row and column"),
        bottom: w(
          plan.of === "column"
            ? "base of the column"
            : plan.of === "row"
              ? "base of the row"
              : plan.of === "all"
                ? "base of the whole table"
                : "base: people who answered"
        ),
      }
    case "count":
      return {
        top:
          plan.stat === "base n" || plan.stat === "base N"
            ? w("people in the base")
            : plan.stat === "N"
              ? "weighted count in this row and column"
              : "people in this row and column",
      }
  }
}

const Calculation: React.FC<{ check: CheckState; value?: string; weight: TraceVariable[] }> = ({
  check,
  value,
  weight,
}) => {
  if (check.status === "running")
    return (
      <div className="flex items-center gap-2 text-[12px] text-slate-500" role="status">
        <Loader fontSize={12} color={ACCENT} />
        Working it out
      </div>
    )
  if (check.status === "error")
    return <p className="text-[12px] text-slate-500">Couldn&apos;t work out the calculation: {check.message}</p>
  const { numbers } = check
  const { plan } = numbers
  const words = sumWords(numbers)
  const decimals = shownDecimals(value, plan.kind === "count" && !plan.weighted ? 0 : 1)
  const result = plan.percent
    ? `${formatNumber(numbers.result * 100, decimals)}%`
    : formatNumber(numbers.result, decimals)
  const matches = matchesShown(value, numbers.result, plan.percent)
  const rows: { sign: string; number: string; words: string; total?: boolean }[] =
    numbers.denominator === undefined
      ? [{ sign: "=", number: formatNumber(numbers.numerator), words: words.top, total: true }]
      : [
          { sign: "", number: formatNumber(numbers.numerator), words: words.top },
          { sign: "÷", number: formatNumber(numbers.denominator), words: words.bottom ?? "" },
          { sign: "=", number: result, words: "", total: true },
        ]
  const people =
    numbers.people !== undefined
      ? `${formatNumber(numbers.people)} ${numbers.people === 1 ? "person" : "people"}${
          plan.weighted && plan.kind === "mean" && numbers.denominator !== undefined
            ? `, weighted ${formatNumber(numbers.denominator)}`
            : ""
        }`
      : null
  return (
    <div className="rounded border border-slate-200 px-3 py-2.5">
      <table className="w-full border-collapse text-[13px]">
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className={r.total ? "border-t border-slate-300" : undefined}>
              <td className="w-4 py-0.5 pr-1 text-slate-400">{r.sign}</td>
              <td className="py-0.5 pr-3 text-right font-medium tabular-nums text-slate-800">{r.number}</td>
              <td className="py-0.5 text-[12px] text-slate-500">{r.words}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {matches === true && <p className="mt-1.5 text-[12px] text-slate-500">Matches the table.</p>}
      {matches === false && (
        <p className="mt-1.5 text-[12px] text-amber-700">
          The table shows {value}, but this works out to {result}.
        </p>
      )}
      <div className="mt-1.5 space-y-0.5 text-[12px] text-slate-500">
        {people && <div>Base: {people}</div>}
        <div>{weight.length > 0 ? `Weighted by ${weight.map((v) => v.label).join(", ")}` : "Not weighted"}</div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// The panel
// ---------------------------------------------------------------------------

const Scripts: React.FC<{ trace: TableTrace; ctx: Context }> = ({ trace, ctx }) => {
  const [open, setOpen] = useState(false)
  return (
    <>
      <Heading>Scripts involved</Heading>
      <Toggle open={open} onClick={() => setOpen((o) => !o)}>
        {open ? "Hide the scripts" : `Show the ${trace.scripts.length} script${trace.scripts.length === 1 ? "" : "s"}`}
      </Toggle>
      {open && (
        <ul className="mt-1.5 space-y-1">
          {trace.scripts.map((index) => (
            <li key={index} className="text-[12px]">
              <details>
                <summary className="cursor-pointer text-slate-700">{ctx.labelOf(index)}</summary>
                <pre className="mt-1 max-h-48 overflow-auto rounded border border-slate-200 bg-slate-50 px-2 py-1.5 font-mono text-[11px] text-slate-700">
                  {ctx.textOf(index)}
                </pre>
              </details>
            </li>
          ))}
        </ul>
      )}
    </>
  )
}

const TraceBody: React.FC<{
  trace: TableTrace
  request: TraceRequest
  ctx: Context
  check: CheckState | null
}> = ({ trace, request, ctx, check }) => {
  const { down, across } = request
  const statLeaf = down.stat !== undefined ? down : across
  const stat = statLeaf.statLabel ?? statLeaf.stat
  const where = [leafLabels(down) || "Total", stat, leafLabels(across) || "Total"].filter(Boolean).join(" · ")
  const transposed = request.transposed === true
  return (
    <div>
      <div>
        {request.value && (
          <div className="text-[28px] font-semibold leading-tight tabular-nums text-slate-900">{request.value}</div>
        )}
        <div className="mt-0.5 text-[13px] text-slate-600">{where}</div>
        {request.title && <div className="mt-0.5 text-[12px] text-slate-400">{request.title}</div>}
        {request.note && <p className="mt-1.5 text-[12px] text-slate-500">{request.note}</p>}
        {!trace.confirmed && (
          <p className="mt-1.5 text-[12px] text-amber-700">
            No record in the session matches this table as shown, so the last definition recorded is traced.
          </p>
        )}
      </div>

      {check && (
        <>
          <Heading>How it was worked out</Heading>
          <Calculation check={check} value={request.value} weight={trace.weight} />
        </>
      )}

      <Heading>Where the values come from</Heading>
      <SideChain
        side="Row"
        leaf={down}
        category={transposed ? trace.columnCategory : trace.rowCategory}
        reads={transposed ? trace.columnReads : trace.rowReads}
        ctx={ctx}
      />
      <SideChain
        side="Column"
        leaf={across}
        category={transposed ? trace.rowCategory : trace.columnCategory}
        reads={transposed ? trace.rowReads : trace.columnReads}
        ctx={ctx}
      />

      <Heading>Base and weight</Heading>
      <div className="space-y-1">
        <CompactLine title="Base" empty="everyone in the data" variables={trace.base} ctx={ctx} />
        <CompactLine title="Weight" empty="not weighted" variables={trace.weight} ctx={ctx} />
        {trace.other.length > 0 && (
          <CompactLine title="Also reads" empty="" variables={trace.other.map((s) => s.variable)} ctx={ctx} />
        )}
      </div>

      {ctx.calls.length > 0 && trace.scripts.length > 0 && <Scripts trace={trace} ctx={ctx} />}
    </div>
  )
}

const errorText = (e: unknown): string => (e instanceof Error && e.message ? e.message : "the check failed.")

/**
 * "Where this number comes from": a side panel beside a table, for the cell clicked. It reads the
 * session's lineage when a cell is clicked (not kept fresh) and traces the cell: the number, how
 * it was worked out (a check of the cell run in the same session once the lineage is in, when the
 * feature can run one), where the values of its row and column come from back to the
 * questionnaire, the base and weight, and the scripts involved. "Open the graph" opens the full
 * lineage window on the table. With `runExclusive` every read of the lineage waits its turn with
 * the session's other calls, so the read, the check and the feature's runs never overlap.
 */
export const TraceNumberPanel: React.FC<TraceNumberPanelProps> = ({
  request,
  onClose,
  instance,
  projectId,
  describeHelper,
  describeScript,
  preparation,
  onRunAgain,
  runCheck,
  runExclusive,
}) => {
  const [graphOpen, setGraphOpen] = useState(false)
  // Read on each click, never in the background: the session changes with every script.
  const lineage = useSessionLineageQuery(instance, false, runExclusive)
  const qnrMeta = useQuestionnaireMetadataQuery(projectId, request !== null)
  const { refetch } = lineage
  // Once per click: StrictMode runs the effect twice, and each read is a call on the session.
  const readFor = useRef<{ request: TraceRequest; instance: string } | null>(null)
  useEffect(() => {
    if (!request || !instance) return
    const last = readFor.current
    if (last?.request === request && last.instance === instance) return
    readFor.current = { request, instance }
    void refetch()
  }, [request, instance, refetch])

  const trace = useMemo(
    () =>
      request && lineage.data
        ? traceTable(lineage.data, request.table, request.down, request.across, {
            tableScript: request.tableScript,
            transposed: request.transposed,
            describeHelper,
          })
        : null,
    [request, lineage.data, describeHelper]
  )

  const ctx = useMemo<Context>(() => {
    const log = lineage.data?.log
    const calls = lineage.data ? sessionCalls(lineage.data) : []
    return {
      qnrIndex: qnrMeta.data?.hasQnr ? fieldIndexOf(qnrMeta.data.fields) : null,
      calls,
      log,
      labelOf: (index: number) =>
        scriptLabel(calls, index, { tableScript: trace?.script, preparation, describe: describeScript }),
      textOf: (index: number) => {
        const call = calls[index]
        if (!call) return ""
        // A schema 3 script is the text as it was sent. A call of the command log is its commands:
        // the panel shows the client's own script instead where it has it (the table's, a part of
        // the preparation), and a data load by the SHA-256 of the data.
        if (!log) return call.text
        if (call.kind === "create")
          return (call.entries ?? []).map(({ entry }) => `Data loaded, SHA-256 ${entry.dataHash ?? ""}`).join("\n")
        if (index === trace?.script && trace.confirmed && request?.tableScript) return request.tableScript
        return preparationPart(call.text, preparation)?.script ?? call.text
      },
    }
  }, [lineage.data, qnrMeta.data, trace, preparation, describeScript, request])

  // The check of the cell: built once the lineage is in, run once per cell.
  const weighted = (trace?.weight.length ?? 0) > 0
  const job = useMemo(() => {
    if (!request?.tableScript || !trace || !runCheck || lineage.isFetching) return null
    const plan = checkPlan(request.down.stat ?? request.across.stat, weighted)
    if (!plan) return null
    const restore = preparationWeight((preparation ?? []).map((p) => p.script))
    const script = traceCheckScript(request.tableScript, request.table, plan, restore)
    return script ? { plan, script } : null
  }, [request, trace, weighted, runCheck, lineage.isFetching, preparation])
  const [check, setCheck] = useState<CheckState | null>(null)
  const runs = useRef<{ request: TraceRequest | null; byScript: Map<string, Promise<EngineOutputItem[]>> }>({
    request: null,
    byScript: new Map(),
  })
  useEffect(() => {
    if (runs.current.request !== request) {
      runs.current = { request, byScript: new Map() }
      setCheck(null)
    }
    if (!job || !request || !runCheck) return
    let live = true
    const { byScript } = runs.current
    let run = byScript.get(job.script)
    if (!run) {
      run = runCheck(job.script)
      byScript.set(job.script, run)
    }
    setCheck({ status: "running" })
    run.then(
      (output) => {
        if (!live) return
        try {
          setCheck({
            status: "done",
            numbers: readCheck(output, job.plan, request.down, request.across, request.transposed === true),
          })
        } catch (e) {
          setCheck({ status: "error", message: errorText(e) })
        }
      },
      (e: unknown) => {
        if (live) setCheck({ status: "error", message: errorText(e) })
      }
    )
    return () => {
      live = false
    }
  }, [job, request, runCheck])

  const body = ((): React.ReactNode => {
    if (!request) return null
    if (!instance)
      return <p className="text-[13px] text-slate-500">The data session is not open, so there is nothing to trace.</p>
    if (lineage.isFetching || (!lineage.data && !lineage.isError))
      return (
        <div className="flex justify-center py-10">
          <Loader fontSize={28} color={ACCENT} />
        </div>
      )
    if (lineage.isError || !lineage.data)
      return <p className="text-[13px] text-slate-500">Couldn&apos;t load the lineage of this session.</p>
    if (!trace)
      return (
        <div className="text-[13px] text-slate-600">
          <p>This table is no longer in the session&apos;s lineage: the session was started again since it was shown.</p>
          {onRunAgain && (
            <Button size="small" className="mt-2" onClick={onRunAgain}>
              Run it again
            </Button>
          )}
        </div>
      )
    return <TraceBody trace={trace} request={request} ctx={ctx} check={job ? check : null} />
  })()

  return (
    <>
      <Drawer
        open={request !== null}
        onClose={onClose}
        placement="right"
        width={420}
        mask={false}
        title="Where this number comes from"
        extra={
          trace ? (
            <Button size="small" onClick={() => setGraphOpen(true)}>
              Open the graph
            </Button>
          ) : null
        }
      >
        {body}
      </Drawer>
      {graphOpen && trace && (
        <LineageModal
          open
          onClose={() => setGraphOpen(false)}
          queryInstance={instance}
          projectId={projectId}
          focusId={trace.table}
          describeHelper={describeHelper}
          scriptLabels={{ tableScript: trace.script, preparation, describe: describeScript }}
          runExclusive={runExclusive}
        />
      )}
    </>
  )
}

export default TraceNumberPanel
